import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api } from "../api/client.ts";
import { useAuth } from "../auth/AuthProvider.tsx";

export function AccountPage() {
  const { user } = useAuth();

  return (
    <div className="page">
      <div className="page-header">
        <h1>Account</h1>
        <p>Your sign-in details.</p>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Profile</h3>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="field">
              <span className="label-mono">Name</span>
              <div style={{ fontWeight: 600 }}>{user?.name}</div>
            </div>
            <div className="field">
              <span className="label-mono">Email</span>
              <div className="mono">{user?.email}</div>
            </div>
            <div className="field">
              <span className="label-mono">Role</span>
              <div>
                <span className={`pill ${user?.role === "admin" ? "info" : "mute"}`}>
                  {user?.role}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ChangePassword />
    </div>
  );
}

function ChangePassword() {
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () => api.post("/auth/password", { currentPassword, newPassword }),
    onSuccess: () => {
      setCurrent("");
      setNew("");
      setConfirm("");
      setDone(true);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Change password</h3>
          <p>Changing it signs you out everywhere else. This browser stays signed in.</p>
        </div>
      </div>
      <form
        className="card-body"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          setDone(false);
          // Caught here rather than by the API, which never sees the confirmation.
          if (newPassword !== confirm) {
            setError("The new passwords don't match.");
            return;
          }
          change.mutate();
        }}
      >
        {error && <div className="error">{error}</div>}
        {done && <div className="notice ok">Password changed.</div>}
        <div className="field">
          <label htmlFor="pw-current">Current password</label>
          <input
            id="pw-current"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="pw-new">New password</label>
            <input
              id="pw-new"
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              required
            />
            <div className="hint">At least 12 characters.</div>
          </div>
          <div className="field">
            <label htmlFor="pw-confirm">Confirm new password</label>
            <input
              id="pw-confirm"
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
        </div>
        <button className="btn small" type="submit" disabled={change.isPending}>
          {change.isPending ? "Changing…" : "Change password"}
        </button>
      </form>
    </div>
  );
}
